// Sentry setup — captures runtime errors from owner desktop + guard mobile
// browsers and ships them to Sentry for triage. Pairs with sourcemap upload
// (see scripts in package.json) so stack traces show real file/line.
//
// Skipped in dev so HMR/strict-mode noise doesn't drown the dashboard.

import * as Sentry from "@sentry/react";

const DSN = "https://26344cb7603c2a75395952063c8df2a2@o4511072331628544.ingest.us.sentry.io/4511321039634432";

export function initSentry() {
  if (process.env.NODE_ENV !== "production") return;
  Sentry.init({
    dsn: DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Tag every event with the React build version. Sourcemap upload uses
    // the same release identifier so traces line up.
    release: process.env.REACT_APP_VERSION || `veloceflow@${process.env.npm_package_version || "dev"}`,
    environment: process.env.REACT_APP_ENV || "production",
    // Performance — sample 10% so we don't flood the project quota.
    tracesSampleRate: 0.1,
    // Session Replay — record on errors only (cheaper). Replay shows the user's
    // actions leading to the crash.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Filter known noise.
    ignoreErrors: [
      // Network blips during navigation
      "Network request failed",
      "Failed to fetch",
      // Firestore "permission denied" can be expected when a user signs out
      // mid-listener; we handle the UX. Don't dump it to Sentry.
      /permission-denied/i,
      // Browser extensions throwing into our window
      /chrome-extension:\/\//,
      // Auto-cancelled fetch on unmount
      "AbortError",
    ],
    beforeSend(event) {
      // Drop events from localhost just in case NODE_ENV check fails.
      const url = event.request?.url || "";
      if (url.includes("localhost") || url.includes("127.0.0.1")) return null;
      return event;
    },
  });
}

/**
 * Tag the current Sentry scope with user context so error reports show
 * which guard/owner/company hit the bug. Call from AuthContext on login,
 * and clearSentryUser() on logout.
 */
export function setSentryUser(profile) {
  if (!profile) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: profile.id,
    username: profile.name || undefined,
    email: profile.email || undefined,
  });
  Sentry.setTag("role", profile.role || "unknown");
  Sentry.setTag("companyId", profile.companyId || "none");
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

/** Wrap the app in an error boundary so React render errors are captured. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
